import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { KeyRound } from "lucide-react";
import { ContentPanel } from "./shared";

interface Props {
  description: string;
  forced?: boolean;
  onCancel?: () => void;
}

export default function PasswordGate({ description, forced = false, onCancel }: Props) {
  const changePassword = useMutation(api.auth.changePassword);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMessage("");
    if (newPassword.length < 8) {
      setStatus("error");
      setMessage("Use at least 8 characters for the new password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus("error");
      setMessage("Passwords do not match.");
      return;
    }
    setStatus("saving");
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setStatus("saved");
      setMessage("Password updated.");
    } catch (err) {
      setStatus("error");
      setMessage(
        err instanceof Error && err.message.includes("InvalidSecret")
          ? "Current password is incorrect."
          : "Could not update password.",
      );
    }
  }

  return (
    <ContentPanel
      title={forced ? "Create your password" : "Change password"}
      eyebrow="Account security"
    >
      <p className="muted-copy">{description}</p>
      <form className="password-form" onSubmit={handleSubmit}>
        <label>
          <span>{forced ? "Temporary password" : "Current password"}</span>
          <input
            autoComplete="current-password"
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder={forced ? "Temporary password" : "Current password"}
            type="password"
            value={currentPassword}
          />
        </label>
        <label>
          <span>New password</span>
          <input
            autoComplete="new-password"
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password"
            type="password"
            value={newPassword}
          />
        </label>
        <label>
          <span>Confirm password</span>
          <input
            autoComplete="new-password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
            type="password"
            value={confirmPassword}
          />
        </label>
        <div className="stem-form-actions">
          {message ? <span>{message}</span> : null}
          {onCancel ? (
            <button onClick={onCancel} type="button">
              Sign out
            </button>
          ) : null}
          <button
            disabled={!currentPassword || !newPassword || !confirmPassword || status === "saving"}
            type="submit"
          >
            <KeyRound className="h-4 w-4" />
            {status === "saving" ? "Updating" : "Update password"}
          </button>
        </div>
      </form>
    </ContentPanel>
  );
}